import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { boldIntlText } from '../../common/views/bold/boldIntlText';
import { formatShortDay } from '../presave/presaveDates';
import { PublishedPageKind } from './publishedPageKind';
import styles from './SMLPublishedLede.module.scss';

interface SMLPublishedLedeProps {
  /** The release the page is about, or the page's name when we know no release. */
  title: string;
  kind: PublishedPageKind;
  /** ISO 8601. Only the pre-save line uses it, and only when it is known. */
  releaseDate: string | null;
}

/**
 * What went live, and what happens now.
 *
 * The old modal quoted the internal page name back at the artist. This names
 * the release and then says the one thing that is true of this kind of page —
 * a pre-save with a date goes Out now by itself on release day (the release-day
 * job of music24-api finds the song and releases the campaign; when it is not
 * sure, it emails the artist); with no date the artist clicks Release now. A
 * release page routes each fan to their own service.
 */
const SMLPublishedLedeComponent: React.FC<SMLPublishedLedeProps> = ({ title, kind, releaseDate }) => {
  const intl = useIntl();

  return (
    <div className={styles.lede}>
      <h2 className={styles.title}>
        {intl.formatMessage({ id: 'app.sml.published.title', defaultMessage: '{title} is live' }, { title })}
      </h2>
      <p className={styles.line}>
        {kind === PublishedPageKind.presave &&
          (releaseDate === null
            ? intl.formatMessage(
                {
                  id: 'app.sml.published.lede.presave_no_date',
                  defaultMessage:
                    'Fans can pre-save it now. When the release is out, click <bold>Release now</bold> to make the page Out now.',
                },
                { bold: boldIntlText }
              )
            : intl.formatMessage(
                {
                  id: 'app.sml.published.lede.presaveAuto',
                  defaultMessage:
                    'Fans can pre-save it now. On {date}, MUSIC24 finds the song on the services and makes the page <bold>Out now</bold>. If we cannot find it, we email you.',
                },
                { date: formatShortDay(intl, releaseDate), bold: boldIntlText }
              ))}
        {kind === PublishedPageKind.release &&
          intl.formatMessage(
            {
              id: 'app.sml.published.lede.release',
              defaultMessage: 'Anyone who opens it lands on <bold>their own</bold> music service in one tap.',
            },
            { bold: boldIntlText }
          )}
        {kind === PublishedPageKind.bioLink &&
          intl.formatMessage({
            id: 'app.sml.published.lede.bio',
            defaultMessage:
              'This is the address for every profile you have. Blocks you add later go live as you publish.',
          })}
      </p>
    </div>
  );
};

export const SMLPublishedLede = TypedMemo(SMLPublishedLedeComponent);
